import React, { type ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { colors, spacing, typography } from '../../core/theme/tokens';

type Props = {
  content: string;
  /** Streaming caret appended after content. */
  cursor?: boolean;
};

/**
 * Lightweight markdown for assistant bubbles: paragraphs, lists, bold, inline code.
 * No dependency — enough for DeepSeek/OpenAI finance replies.
 */
export function ChatMarkdown({ content, cursor }: Props) {
  const blocks = splitBlocks(content.trimEnd());
  return (
    <View style={styles.wrap}>
      {blocks.map((block, i) => (
        <View key={`${block.kind}-${i}`} style={styles.block}>
          {renderBlock(block)}
        </View>
      ))}
      {cursor ? <Text style={styles.cursor}>▍</Text> : null}
    </View>
  );
}

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] };

function splitBlocks(src: string): Block[] {
  if (!src) return [];
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: Block[] = [];
  let para: string[] = [];
  let ul: string[] | null = null;
  let ol: string[] | null = null;

  const flushPara = () => {
    if (!para.length) return;
    out.push({ kind: 'p', text: para.join(' ').trim() });
    para = [];
  };
  const flushLists = () => {
    if (ul?.length) out.push({ kind: 'ul', items: ul });
    if (ol?.length) out.push({ kind: 'ol', items: ol });
    ul = null;
    ol = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.+)$/);
    const numbered = line.match(/^\s*(\d+)[.)]\s+(.+)$/);
    if (!line.trim()) {
      flushPara();
      flushLists();
      continue;
    }
    if (bullet) {
      flushPara();
      if (ol) {
        out.push({ kind: 'ol', items: ol });
        ol = null;
      }
      ul = ul ?? [];
      ul.push(bullet[1]!);
      continue;
    }
    if (numbered) {
      flushPara();
      if (ul) {
        out.push({ kind: 'ul', items: ul });
        ul = null;
      }
      ol = ol ?? [];
      ol.push(numbered[2]!);
      continue;
    }
    flushLists();
    para.push(line.trim());
  }
  flushPara();
  flushLists();
  return out;
}

function renderBlock(block: Block): ReactNode {
  if (block.kind === 'p') {
    return <Text style={styles.p}>{renderInline(block.text)}</Text>;
  }
  if (block.kind === 'ul') {
    return (
      <View style={styles.list}>
        {block.items.map((item, i) => (
          <View key={i} style={styles.listRow}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.listText}>{renderInline(item)}</Text>
          </View>
        ))}
      </View>
    );
  }
  return (
    <View style={styles.list}>
      {block.items.map((item, i) => (
        <View key={i} style={styles.listRow}>
          <Text style={styles.olIndex}>{i + 1}.</Text>
          <Text style={styles.listText}>{renderInline(item)}</Text>
        </View>
      ))}
    </View>
  );
}

/** Inline: `code`, **bold**, *italic* (non-greedy). */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) {
      nodes.push(<Text key={`t-${key++}`}>{text.slice(last, m.index)}</Text>);
    }
    const token = m[0]!;
    if (token.startsWith('`')) {
      nodes.push(
        <Text key={`c-${key++}`} style={styles.code}>
          {token.slice(1, -1)}
        </Text>,
      );
    } else if (token.startsWith('**')) {
      nodes.push(
        <Text key={`b-${key++}`} style={styles.bold}>
          {token.slice(2, -2)}
        </Text>,
      );
    } else {
      nodes.push(
        <Text key={`i-${key++}`} style={styles.italic}>
          {token.slice(1, -1)}
        </Text>,
      );
    }
    last = m.index + token.length;
  }
  if (last < text.length) {
    nodes.push(<Text key={`t-${key++}`}>{text.slice(last)}</Text>);
  }
  return nodes;
}

const styles = StyleSheet.create({
  wrap: { gap: spacing.sm },
  block: {},
  p: {
    ...typography.body,
    fontSize: 15,
    lineHeight: 23,
    color: colors.text,
  },
  list: { gap: 6 },
  listRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  bullet: {
    ...typography.body,
    fontSize: 15,
    lineHeight: 23,
    color: colors.accent,
    width: 14,
  },
  olIndex: {
    ...typography.body,
    fontSize: 15,
    lineHeight: 23,
    color: colors.accent,
    fontWeight: '700',
    width: 22,
    fontVariant: ['tabular-nums'],
  },
  listText: {
    ...typography.body,
    flex: 1,
    fontSize: 15,
    lineHeight: 23,
    color: colors.text,
  },
  bold: { fontWeight: '700', color: colors.text },
  italic: { fontStyle: 'italic', color: colors.text },
  code: {
    fontFamily: 'Menlo, Monaco, Consolas, monospace',
    fontSize: 13,
    color: colors.accent,
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
  },
  cursor: {
    ...typography.body,
    fontSize: 15,
    color: colors.accent,
    marginTop: 2,
  },
});
